import React from 'react'

import Image from 'next/image'

import { Column, Row, Text } from 'Components/UI'

import { ArrowRightGlyph, CheckGlyph } from 'Assets/Svg/Icons'

import { LANDING_INLINE_PADDINGS } from 'Constants/Landing'
import { PROJECT } from 'Constants/Paths'

import { ProjectPreview } from 'Types/projectPreview'

import {
  BenefitHolder,
  Container,
  ContainerProps,
  ImageHolder,
  LogoHolder,
  StyledLink,
} from './styles'

type Props = ContainerProps & {
  data: ProjectPreview
  secondary?: boolean
}

// ImageHolder caps the screenshot at 720px. Measured: 1290px+ -> 720 CSS px,
// 1024 -> 481 (two-column stage), 768 -> 720, 412 -> full width.
const PREVIEW_IMAGE_SIZES =
  '(min-width: 1290px) 720px, (min-width: 992px) 50vw, (min-width: 768px) 94vw, 100vw'

function ProjectPreview({ data, isFirst, secondary }: Props) {
  if (!data) return null

  return (
    <Container
      flexDirection={['column', 'column', 'column', 'column', 'row']}
      isFirst={isFirst}
      pt={secondary ? 17 : 0}
    >
      <Column
        fullHeight
        fullWidth
        gap={11}
        maxWidth={['100%', '100%', '100%', '100%', '540px']}
        mb={['-40px', '-40px', '-40px', '-40px', 0]}
        pb={[11, 11, 11, 11, 17]}
        pl={LANDING_INLINE_PADDINGS}
        // responsive value in place of the old isDesktop JS gate (SSR-safe)
        pr={[...LANDING_INLINE_PADDINGS, 0]}
        pt={secondary ? 0 : 17}
        spaceBetween
      >
        <Column gap={9}>
          <Row gap={5}>
            <LogoHolder>
              <Image alt={`Logo - ${data.company}`} fill src={data.logoSrc} />
            </LogoHolder>

            <Column>
              <Text body16 medium title>
                {data.company}
              </Text>

              {data.industry && (
                <Text body12 medium muted>
                  {data.industry}
                </Text>
              )}
            </Column>
          </Row>

          {data.title && (
            <Column>
              <Text as="h3" medium title title28>
                {data.title}
              </Text>
            </Column>
          )}

          <Column gap={5}>
            <Text body body16 semiLight>
              {data.description}
            </Text>

            {!!data.tags?.length && (
              <Row gap={4}>
                {data.tags.map(item => (
                  <Text body14 key={item} linkColor medium>
                    #{item}
                  </Text>
                ))}
              </Row>
            )}
          </Column>

          {!!data.benefits.length && (
            <Column gap={5}>
              {data.benefits.map(item => (
                <BenefitHolder key={item}>
                  <CheckGlyph />

                  <Text body body14 medium>
                    {item}
                  </Text>
                </BenefitHolder>
              ))}
            </Column>
          )}
        </Column>

        <StyledLink
          aria-label={`Learn more about ${data.company}`}
          href={PROJECT(data.slug)}
        >
          <Text as="span" body16 medium>
            Learn more
          </Text>

          {ArrowRightGlyph()}
        </StyledLink>
      </Column>

      <ImageHolder>
        <Image
          alt={`Screenshots of application ${data.company}`}
          fill
          // The first preview is the LCP element on /work; without this it is
          // lazy-loaded and the load delay dominates LCP.
          priority={isFirst}
          sizes={PREVIEW_IMAGE_SIZES}
          src={data.imageSrc}
        />
      </ImageHolder>
    </Container>
  )
}

export default ProjectPreview
